/**
 * Privé Favoritos — the hearts (owner, 2026-10-23: Privé's own wishlist in
 * place of the HulkApps app, the same list as the iOS app's Favoritos).
 *
 * Where the list lives:
 * - Signed in: in the customer's Shopify account, the metafield
 *   `prive_app.favoritos` (newest first, 128 at most). The layout draws it
 *   into #prive-favoritos-data; a change goes to prive-customer-brain's
 *   /api/favoritos with the customer token the layout signs, and the answer
 *   is the list as the account now has it, the app's changes included.
 * - Guest: in this browser. On the first page after signing in, it joins
 *   the account and the browser copy is cleared.
 *
 * A heart never waits on the network. It changes at once and the change is
 * kept here until the account confirms it, so a slow or failed request, a
 * closed tab or Brain being down loses nothing: it is sent again on the
 * next page.
 */
(function () {
  'use strict';

  if (window.PriveFavoritos) return;

  var API = 'https://prive-customer-brain.fly.dev/api/favoritos';
  var GUEST_KEY = 'prive_favoritos_v1';
  var ACCOUNT_KEY = 'prive_favoritos_account_v1';
  /** The answer this tab last drew a page afresh for (sessionStorage). */
  var REDRAWN_KEY = 'prive_favoritos_redrawn';
  var LIMIT = 128;
  var ID_RE = /^\d{1,20}$/;
  var HANDLE_RE = /^[a-z0-9][a-z0-9-]{0,254}$/;
  var RETRY_MS = [15000, 60000, 300000];
  /** Liquid gives the first 50 of a reference list without `paginate`. */
  var FIRST = 50;
  /** Tries for a section that did not come: the whole list, or a card. */
  var TRIES = 3;
  /**
   * How long a request may go unanswered, on a stalled connection, before it
   * is given up on: one to Shopify for a section, and a send to the account,
   * which reads and writes in Shopify before it answers.
   */
  var WAIT_MS = 10000;
  var SEND_MS = 20000;
  /** A little past a second Shopify's clock has gone by, for timers that run early. */
  var TICK_MS = 250;

  // ── Pure list rules (tested in test/prive-favoritos.test.js) ──────────

  function cleanIds(list) {
    var out = [];
    (Array.isArray(list) ? list : []).forEach(function (id) {
      id = String(id);
      if (ID_RE.test(id) && out.indexOf(id) === -1) out.push(id);
    });
    return out.slice(0, LIMIT);
  }

  /**
   * Later taps on the same product replace earlier ones; order is tap order.
   * Each tap is told apart, so one like a tap being sent is still its own.
   */
  var taps = 0;
  function setPending(pending, id, op) {
    var next = pending.filter(function (p) { return p.id !== id; });
    next.push({ id: id, op: op, tap: Date.now().toString(36) + '.' + (taps += 1) + '.' + Math.random().toString(36).slice(2, 8) });
    return next;
  }

  /** The list the customer sees: what the account last said, plus the taps it has not confirmed. */
  function applyPending(base, pending) {
    var list = base.slice();
    pending.forEach(function (p) {
      var at = list.indexOf(p.id);
      if (p.op === 'add' && at === -1) list.unshift(p.id);
      if (p.op === 'remove' && at !== -1) list.splice(at, 1);
    });
    return list.slice(0, LIMIT);
  }

  /** Taps the account has answered for drop out; a tap made since stays, even one alike. */
  function settle(pending, sent) {
    return pending.filter(function (p) {
      return !sent.some(function (s) { return s.id === p.id && s.op === p.op && s.tap === p.tap; });
    });
  }

  /**
   * The base to start from on a signed-in page. The page carries at most the
   * newest 50 and the total. The copy kept here wins when the account gave
   * it after this page was drawn (another tab, or a page from the back-forward
   * cache), or when it agrees with what the page shows and has more of it.
   * Otherwise the page is newer: the app may have changed the list since.
   */
  function startingBase(page, kept, pageAt) {
    var pageIds = cleanIds(page.ids);
    if (!kept || !Array.isArray(kept.ids)) return pageIds;
    if (Number(kept.at) > pageAt) return cleanIds(kept.ids);
    if (kept.ids.length === page.count && kept.ids.length > pageIds.length) {
      var agrees = pageIds.every(function (id, i) { return kept.ids[i] === id; });
      if (agrees) return cleanIds(kept.ids);
    }
    return pageIds;
  }

  /**
   * A base cut at the page's 50 with the cards the Favoritos page drew past
   * them (its second page on): those are saved too. In the list's order.
   */
  function withDrawn(base, drawn) {
    var list = base.slice();
    cleanIds(drawn).forEach(function (id) { if (list.indexOf(id) === -1) list.push(id); });
    return list.slice(0, LIMIT);
  }

  // ── Storage ───────────────────────────────────────────────────────────

  function read(key) {
    try { return JSON.parse(window.localStorage.getItem(key) || 'null'); } catch (e) { return null; }
  }
  /**
   * False while this browser keeps nothing: this tab's copy is then the only
   * one. A write that goes through writes all of it, so it is true again.
   */
  var storable = true;
  function write(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
      storable = true;
    } catch (e) {
      storable = false;
    }
    return storable;
  }
  function drop(key) {
    try { window.localStorage.removeItem(key); } catch (e) {}
  }
  /**
   * `request` (made with `abort`'s signal, where the browser has one), or a
   * failure once `ms` have gone by without an answer, so a request that never
   * answers never holds the next one up.
   */
  function timed(request, ms, abort) {
    var timer;
    var late = new Promise(function (_, fail) {
      timer = setTimeout(function () {
        if (abort) abort.abort();
        fail(new Error('timeout'));
      }, ms);
    });
    return Promise.race([request, late]).then(function (value) {
      clearTimeout(timer);
      return value;
    }, function (error) {
      clearTimeout(timer);
      throw error;
    });
  }
  function aborter() {
    return typeof window.AbortController === 'function' ? new window.AbortController() : null;
  }
  function meta(name) {
    var tag = document.querySelector('meta[name="' + name + '"]');
    return tag ? String(tag.content || '') : '';
  }
  /** The cards Shopify drew on the Favoritos page, not the ones added here. */
  function drawnIds() {
    return Array.prototype.map.call(
      document.querySelectorAll('[data-favoritos-grid] .product-item:not([data-favoritos-added]) prive-favorito'),
      function (el) { return el.getAttribute('product-id'); }
    );
  }
  /** The storefront's root in the shopper's language: `/`, or `/en/`. */
  function storefrontRoot() {
    var routes = window.Shopify && window.Shopify.routes;
    return (routes && typeof routes.root === 'string' && routes.root) || '/';
  }
  function pageData() {
    var el = document.getElementById('prive-favoritos-data');
    try { return el ? JSON.parse(el.textContent) : null; } catch (e) { return null; }
  }
  /** The list as the page data shows it: its total and its first 50. */
  function signature(data) {
    return (Number(data.count) || 0) + ':' + cleanIds(data.ids).join(',');
  }

  // ── State ─────────────────────────────────────────────────────────────

  var page = pageData() || { signedIn: false, count: 0, ids: [] };
  /**
   * The times kept here are Shopify's, as near as this page can tell: they
   * are weighed against the server time this page was drawn at, and the
   * device's clock may be off by minutes. The estimate runs behind by the
   * time the page took to arrive, so a close call goes to the page.
   *
   * Two are kept for the list: `at`, a time no later than the account was
   * as it says (the account's answer gives one, taken before its read or
   * write), weighed against when a page was drawn; and `answered`, which
   * orders the account's lists however they arrive: the answer's own time,
   * or the whole list's (when Shopify drew it), so an older one never
   * replaces a newer one. An answer with no time of its own goes by its
   * send's stamp. `confirmed` is the newest answer to a send alone: the
   * account takes a customer's changes in turn, so it holds every change
   * answered before it.
   */
  var drawnAt = Number(meta('prive-live-search-ts')) * 1000 || 0;
  var skew = drawnAt ? Date.now() - drawnAt : 0;
  function serverNow() { return Date.now() - skew; }
  var cid = meta('prive-cid');
  var signedIn = Boolean(page.signedIn && ID_RE.test(cid));
  var state = { ids: [], base: [], pending: [], handles: {}, at: 0, answered: 0, confirmed: 0, stamp: 0, whole: null, complete: false };
  /** The page data's signature while the whole list is still to be asked for. */
  var wanted = null;
  /**
   * Asked for until it comes, a few times at most a page, so tabs never ask
   * on each other's answers.
   */
  var askingWhole = false;
  var wholeTries = 0;
  /**
   * The account answered after this page was drawn with a list other than
   * the one it drew. Shopify says when to the second, so a page drawn just
   * after an answer may look older: it shows the same list.
   */
  var drawnStale = false;
  /** A guest's hearts joined here but kept nowhere else yet. */
  var joining = null;
  var inflight = false;
  var retryAt = 0;
  var retries = 0;
  var flushTimer = null;

  function load() {
    readState();
    askWhole();
  }

  /**
   * The state as this browser keeps it now, which may be newer than this
   * tab's: another tab writes it too. Read again before every change and
   * written after it, so a change one tab makes is never lost to another's.
   */
  function refresh() {
    if (storable) readState(true);
  }

  /**
   * `quiet`: read only. The copy kept here is written at load and after a
   * change; a read that wrote this page's own list back would set off
   * another tab drawn with another list to write its own, and so on.
   */
  function readState(quiet) {
    wanted = null;
    var guest = read(GUEST_KEY) || {};
    var guestIds = cleanIds(guest.ids);
    var handles = guest.handles && typeof guest.handles === 'object' ? guest.handles : {};
    if (!signedIn) {
      state = { ids: guestIds, base: guestIds, pending: [], handles: handles };
      return;
    }
    var kept = read(ACCOUNT_KEY);
    if (!kept || kept.cid !== cid) kept = { cid: cid, ids: [], pending: [], handles: {} };
    var pending = Array.isArray(kept.pending) ? kept.pending.filter(function (p) {
      return p && ID_RE.test(String(p.id)) && (p.op === 'add' || p.op === 'remove');
    }) : [];
    var count = Number(page.count) || 0;
    var whole = kept.whole && typeof kept.whole === 'object' && Array.isArray(kept.whole.ids) ? kept.whole : null;
    var pageAt = drawnAt || serverNow();
    var base = startingBase({ ids: page.ids, count: count }, kept, pageAt);
    var answeredSince = Number(kept.at) > pageAt;
    var keptIds = cleanIds(kept.ids);
    drawnStale = answeredSince && signature({ count: keptIds.length, ids: keptIds.slice(0, FIRST) }) !== signature(page);
    // The account's own list, not the page data (which may stop at 50).
    var complete = base.join(',') !== cleanIds(page.ids).join(',');
    if (count > FIRST && base.length <= FIRST) {
      // The page data stops at the first 50. The rest is the whole list as
      // last asked for, while the page shows that same list and no answer
      // has come since; else it is asked for, so a heart further down shows
      // saved on every page.
      if (whole && whole.sig === signature(page) && !answeredSince) {
        base = cleanIds(whole.ids);
        complete = true;
      } else {
        wanted = signature(page);
      }
    }
    // The cards drawn here are older than an answer that came since.
    if (base.length < count && !answeredSince) base = withDrawn(base, drawnIds());
    // A guest's hearts join the account, oldest first so the newest stays on top.
    guestIds.slice().reverse().forEach(function (id) { pending = setPending(pending, id, 'add'); });
    state = {
      ids: applyPending(base, pending),
      base: base,
      pending: pending,
      handles: Object.assign({}, kept.handles || {}, handles),
      at: Number(kept.at) || 0,
      answered: Number(kept.answered) || 0,
      confirmed: Number(kept.confirmed) || 0,
      stamp: Number(kept.stamp) || 0,
      whole: whole,
      complete: complete,
    };
    // The guest's copy goes once the account's, with its hearts, is kept,
    // or else once the account has taken them.
    if (!quiet && save()) {
      if (guestIds.length) drop(GUEST_KEY);
    } else if (guestIds.length) {
      joining = guestIds;
    }
  }

  /** What `sections/favoritos-ids` draws now, or null. */
  function idsSection() {
    var abort = aborter();
    return timed(fetch(storefrontRoot() + '?section_id=favoritos-ids', { credentials: 'same-origin', cache: 'no-store', signal: abort ? abort.signal : undefined }).then(function (res) {
      return res.ok ? res.text() : '';
    }).then(function (html) {
      var found = /<script[^>]*data-favoritos-ids[^>]*>([\s\S]*?)<\/script>/.exec(html || '');
      return found ? JSON.parse(found[1]) : null;
    }), WAIT_MS, abort);
  }

  /**
   * The whole of a signed-in list, past the page data's 50, from the section
   * that draws it (`sections/favoritos-ids`), with when Shopify drew it. An
   * answer from the account as new or newer is already whole, so it stands;
   * an answer still on its way is weighed by its own time when it comes. One
   * that does not come is asked for again a little later.
   */
  function askWhole() {
    if (!wanted || askingWhole || wholeTries >= TRIES) return;
    var sig = wanted;
    askingWhole = true;
    wholeTries++;
    function failed() {
      askingWhole = false;
      if (wholeTries < TRIES) setTimeout(askWhole, RETRY_MS[wholeTries - 1]);
    }
    idsSection().then(function (data) {
      if (!data || !Array.isArray(data.ids)) { failed(); return; }
      askingWhole = false;
      // Drawn after this page, to the second.
      var drawn = Number(data.at) || drawnAt;
      // Another tab may have changed the list kept here since.
      refresh();
      if (wanted !== sig || state.answered >= drawn) {
        // An answer came after Shopify drew this one. When it still leaves
        // the list past the first 50 unknown, one drawn since is asked for,
        // once the answer's second has gone by: Shopify says when it drew
        // one to the second, so one drawn within it would not count.
        renderAll();
        if (wanted) setTimeout(askWhole, Math.min(RETRY_MS[0], Math.max(0, (Math.floor(state.answered / 1000) + 1) * 1000 - serverNow()) + TICK_MS));
        return;
      }
      wanted = null;
      state.base = cleanIds(data.ids);
      state.whole = { sig: sig, ids: state.base };
      state.complete = true;
      state.answered = drawn;
      state.at = Math.max(state.at, drawn);
      state.ids = applyPending(state.base, state.pending);
      save();
      renderAll();
    }).catch(failed);
  }

  function save() {
    if (signedIn) {
      var kept = write(ACCOUNT_KEY, { cid: cid, ids: state.base, at: state.at, answered: state.answered, confirmed: state.confirmed, stamp: state.stamp, pending: state.pending, handles: state.handles, whole: state.whole });
      // The guest's hearts are in the account's copy now: its own copy goes,
      // or a later read would join them again over a tap made since.
      if (kept && joining) {
        drop(GUEST_KEY);
        joining = null;
      }
      return kept;
    }
    return write(GUEST_KEY, { ids: state.ids, handles: state.handles });
  }

  function has(id) { return state.ids.indexOf(String(id)) !== -1; }

  /** Product ids tapped on this page. */
  var tapped = {};

  function toggle(id, handle) {
    id = String(id);
    if (!ID_RE.test(id)) return;
    tapped[id] = true;
    refresh();
    var adding = !has(id);
    if (adding && HANDLE_RE.test(handle || '')) state.handles[id] = handle;
    if (signedIn) {
      state.pending = setPending(state.pending, id, adding ? 'add' : 'remove');
      state.ids = applyPending(state.base, state.pending);
    } else {
      state.ids = applyPending(state.ids, [{ id: id, op: adding ? 'add' : 'remove' }]);
      state.base = state.ids;
    }
    if (!adding) delete state.handles[id];
    save();
    renderAll();
    announce(adding ? page.added : page.removed);
    publish(adding ? 'prive_favorito_added' : 'prive_favorito_removed', id);
    scheduleFlush(400);
  }

  // ── Sending to the account ────────────────────────────────────────────

  /** A signature fresh from Shopify, once the page's own has run out. */
  var renewed = null;
  function credentials() {
    return renewed || { ts: meta('prive-live-search-ts'), token: meta('prive-live-search-token') };
  }
  /**
   * prive-customer-brain takes a page's signature for a day. A page left open
   * longer asks `sections/favoritos-ids` for one drawn now.
   */
  function renew() {
    return idsSection().then(function (data) {
      if (data && /^\d+$/.test(String(data.ts || '')) && /^[0-9a-f]{64}$/.test(String(data.token || ''))) {
        renewed = { ts: String(data.ts), token: String(data.token) };
      }
    }).catch(function () {});
  }

  function scheduleFlush(delay) {
    if (!signedIn) return;
    clearTimeout(flushTimer);
    flushTimer = setTimeout(flush, delay);
  }

  function flush() {
    if (!signedIn || inflight) return;
    refresh();
    if (state.pending.length === 0) return;
    if (Date.now() < retryAt) { scheduleFlush(retryAt - Date.now()); return; }
    var auth = credentials();
    if (!auth.ts || !auth.token) return;
    var sent = state.pending.slice();
    // Each send is stamped, later than any before it in every tab, so an
    // answer to an older one never replaces a newer one's list.
    var stamp = Math.max(serverNow(), state.stamp + 1);
    state.stamp = stamp;
    save();
    var body = {
      customer_id: cid,
      customer_ts: auth.ts,
      customer_token: auth.token,
      add: sent.filter(function (p) { return p.op === 'add'; }).map(function (p) { return p.id; }),
      remove: sent.filter(function (p) { return p.op === 'remove'; }).map(function (p) { return p.id; }),
    };
    inflight = true;
    var abort = aborter();
    // One that never answers is given up on and waits like a failed one; the
    // change goes again, and the account takes an add or a remove it already
    // has as it is.
    timed(fetch(API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
      signal: abort ? abort.signal : undefined,
    }).then(function (res) {
      if (res.ok) return res.json().then(function (data) { return { ok: true, ids: data && data.ids, at: Number(data && data.at) || 0 }; });
      // A malformed change or an unknown customer will never land: let it go.
      // Anything else (a signature run out, a limit, the service down) waits.
      return { ok: false, drop: res.status === 400 || res.status === 404, refused: res.status === 401 };
    }), SEND_MS, abort).then(function (result) {
      inflight = false;
      refresh();
      if (result.ok && Array.isArray(result.ids)) {
        var order = result.at || stamp;
        if (order >= state.answered) {
          state.base = cleanIds(result.ids);
          state.answered = order;
        } else if (order > state.confirmed) {
          // A whole list Shopify drew after this answer's time is kept here,
          // drawn perhaps while this change was being written: what this one
          // confirms goes over it. After a newer answer (another tab's) it
          // needs nothing: that one already holds this change, or a newer
          // tap over it, and so does a whole list drawn after it.
          state.base = applyPending(state.base, sent);
        }
        state.confirmed = Math.max(state.confirmed, order);
        state.complete = true;
        // When the account was so: the answer says, else it is about now,
        // and either way later than this page, which sent it. A page drawn
        // before it is older, even one drawn after this send.
        state.at = Math.max(state.at, result.at || serverNow(), drawnAt + 1);
        state.pending = settle(state.pending, sent);
        if (joining && joining.every(function (id) { return sent.some(function (p) { return p.id === id; }); })) {
          drop(GUEST_KEY);
          joining = null;
        }
        retries = 0;
        retryAt = 0;
      } else if (result.drop) {
        state.pending = settle(state.pending, sent);
      } else if (result.refused && retries === 0) {
        // The page's signature ran out: sent again at once with a fresh one.
        retries++;
        inflight = true;
        renew().then(function () {
          inflight = false;
          scheduleFlush(0);
        });
      } else {
        if (result.refused) renew();
        backOff();
      }
      state.ids = applyPending(state.base, state.pending);
      save();
      renderAll();
      if (state.pending.length && !retryAt) scheduleFlush(0);
    }, function () {
      inflight = false;
      backOff();
    });
  }

  function backOff() {
    retryAt = Date.now() + RETRY_MS[Math.min(retries, RETRY_MS.length - 1)];
    retries++;
    scheduleFlush(retryAt - Date.now());
  }

  // ── Drawing ───────────────────────────────────────────────────────────

  function sync(el) {
    var button = el.querySelector('.favorito__button');
    if (!button) return;
    var on = has(el.getAttribute('product-id'));
    button.setAttribute('aria-pressed', on ? 'true' : 'false');
    // On the Favoritos page a card let go of stays, faded, until the page is
    // left, so a slip of the thumb is one tap to undo.
    var card = el.closest ? el.closest('[data-favoritos-page] .product-item') : null;
    if (card) card.classList.toggle('is-let-go', !on);
  }

  function renderAll() {
    Array.prototype.forEach.call(document.querySelectorAll('prive-favorito'), sync);
    Array.prototype.forEach.call(document.querySelectorAll('[data-favoritos-link]'), function (link) {
      link.classList.toggle('is-filled', state.ids.length > 0);
    });
    document.dispatchEvent(new CustomEvent('prive:favoritos', { detail: { ids: state.ids.slice() } }));
  }

  var live;
  function announce(text) {
    if (!text) return;
    if (!live) {
      live = document.createElement('div');
      live.className = 'visually-hidden';
      live.setAttribute('aria-live', 'polite');
      document.body.appendChild(live);
    }
    live.textContent = '';
    setTimeout(function () { live.textContent = text; }, 50);
  }

  /** For the analytics pixel; a sale never waits on it. */
  function publish(name, id) {
    try {
      if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
        window.Shopify.analytics.publish(name, { product_id: id, handle: state.handles[id] || null });
      }
    } catch (e) {}
  }

  var PriveFavorito = class extends HTMLElement {
    connectedCallback() {
      sync(this);
      if (this._bound) return;
      var button = this.querySelector('.favorito__button');
      if (!button) return;
      this._bound = true;
      var self = this;
      button.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        toggle(self.getAttribute('product-id'), self.getAttribute('product-handle'));
      });
    }
  };

  // ── The Favoritos page ────────────────────────────────────────────────

  /** Product ids a card is coming or came for here, so none is asked for twice. */
  var carded = {};
  /** How many times a card did not come, by product id. */
  var missed = {};

  /**
   * Whether this is the first Favoritos page, as Shopify drew it: a metafield
   * list's page key is its own (`page_…`), not `page`.
   */
  function isFirst(root) {
    return (Number(root.getAttribute('data-page')) || 1) === 1;
  }
  function onGridIds(grid) {
    var onGrid = {};
    Array.prototype.forEach.call(grid.querySelectorAll('prive-favorito'), function (el) {
      onGrid[el.getAttribute('product-id')] = true;
    });
    return onGrid;
  }
  function renderedIds() {
    var rendered = {};
    cleanIds(page.ids).forEach(function (id) { rendered[id] = true; });
    return rendered;
  }

  /**
   * The cards a Favoritos page lacks: a guest's whole list, or, for the
   * account's list Shopify drew, a heart still on its way to the account, on
   * top of the first page.
   */
  function lacking(root, grid) {
    var first = isFirst(root);
    var onGrid = onGridIds(grid);
    var rendered = renderedIds();
    return state.ids.filter(function (id) {
      if (onGrid[id] || carded[id] || (missed[id] || 0) >= TRIES || !HANDLE_RE.test(state.handles[id] || '')) return false;
      if (!signedIn) return true;
      return first && !rendered[id] && state.pending.some(function (p) { return p.id === id && p.op === 'add'; });
    });
  }

  /**
   * The hearts the account gained since the first Favoritos page was drawn
   * (its newest, above every heart the page drew) that no card can come for
   * here: one saved in the app, say, whose handle this browser never kept.
   */
  function undrawable(root, grid) {
    if (!signedIn || !isFirst(root)) return [];
    var onGrid = onGridIds(grid);
    var rendered = renderedIds();
    var coming = lacking(root, grid);
    var out = [];
    for (var i = 0; i < state.ids.length && !rendered[state.ids[i]]; i++) {
      var id = state.ids[i];
      if (state.base.indexOf(id) !== -1 && !onGrid[id] && !carded[id] && coming.indexOf(id) === -1) out.push(id);
    }
    return out;
  }

  /**
   * Whether the account now keeps the hearts the first Favoritos page drew in
   * another order: one let go and saved again in the app comes back on top.
   * One tapped here stays where the thumb left it.
   */
  function reordered(root, grid) {
    if (!signedIn || !isFirst(root)) return false;
    var rendered = renderedIds();
    var drawn = [];
    Array.prototype.forEach.call(grid.querySelectorAll('prive-favorito'), function (el) {
      var id = el.getAttribute('product-id');
      if (!carded[id] && !tapped[id] && state.base.indexOf(id) !== -1) drawn.push(id);
    });
    var kept = state.base.filter(function (id) {
      return rendered[id] && !carded[id] && !tapped[id];
    }).slice(0, drawn.length);
    return kept.join(',') !== drawn.join(',');
  }

  /**
   * Draws the page afresh, once for each answer from the account (`at`), so
   * a page Shopify draws the same again is not drawn over and over.
   */
  function redrawFor(at) {
    try {
      if (window.sessionStorage.getItem(REDRAWN_KEY) === String(at)) return false;
      window.sessionStorage.setItem(REDRAWN_KEY, String(at));
    } catch (e) {}
    window.location.reload();
    return true;
  }

  /**
   * Each card is the theme's own, rendered by Shopify for that product
   * (`sections/favorito-card`), newest first on top of the grid. One that
   * does not come is let out of `carded`, to be asked for again.
   */
  function addCards(root, grid, ids) {
    if (!ids.length) return Promise.resolve();
    ids.forEach(function (id) { carded[id] = true; });
    root.classList.add('is-loading');
    var slots = ids.map(function () {
      var slot = document.createElement('div');
      slot.className = 'favoritos__slot';
      return slot;
    });
    for (var i = slots.length - 1; i >= 0; i--) grid.insertBefore(slots[i], grid.firstChild);

    var next = 0;
    function worker() {
      if (next >= ids.length) return Promise.resolve();
      var at = next++;
      function missing() {
        slots[at].remove();
        delete carded[ids[at]];
        missed[ids[at]] = (missed[ids[at]] || 0) + 1;
      }
      var url = storefrontRoot() + 'products/' + encodeURIComponent(state.handles[ids[at]]) + '?section_id=favorito-card';
      var abort = aborter();
      return timed(fetch(url, { credentials: 'same-origin', signal: abort ? abort.signal : undefined }).then(function (res) {
        return res.ok ? res.text() : '';
      }), WAIT_MS, abort).then(function (html) {
        var holder = document.createElement('div');
        holder.innerHTML = html;
        var card = holder.querySelector('.product-item');
        if (card) {
          card.setAttribute('data-favoritos-added', '');
          slots[at].replaceWith(card);
        } else {
          missing();
        }
      }, missing).then(worker);
    }
    return Promise.all([worker(), worker(), worker(), worker()]).then(function () {
      root.classList.remove('is-loading');
    });
  }

  /**
   * The page draws a signed-in customer's list itself. Here it fills in what
   * only this browser knows: a guest's whole list, or hearts tapped on the
   * way here that the account has not confirmed; and, while it is open, a
   * heart saved in another tab. A list the cards cannot follow has the page
   * drawn afresh.
   */
  function fillPage(root) {
    var grid = root.querySelector('[data-favoritos-grid]');
    var empty = root.querySelector('[data-favoritos-empty]');
    var countLine = root.querySelector('[data-favoritos-count]');
    if (!grid) return;
    // An answer from the account that came, in another tab, after Shopify
    // drew this page, with another list, makes what it drew stale.
    if (signedIn && drawnAt && drawnStale && redrawFor(state.at)) return;
    function writeCount(n) {
      if (!countLine) return;
      var form = countLine.getAttribute(n === 1 ? 'data-one' : 'data-other') || '';
      countLine.textContent = n > 0 ? form.replace('#', String(n)) : '';
    }
    // A guest's list is all here, and so is the account's once it is its
    // own list. Until then, the account's is what Shopify drew, with the
    // hearts still on their way to it and without the ones let go of on the
    // way, on any page.
    var total = Number(root.getAttribute('data-total')) || 0;
    function counted() {
      if (!signedIn || state.complete) return state.ids.length;
      var gained = state.pending.filter(function (p) { return p.op === 'add' && state.base.indexOf(p.id) === -1; }).length;
      // A heart let go of was saved when it was tapped; while the list here
      // stops at the page data's 50, one past them is not in it.
      var whole = state.base.length >= (Number(page.count) || 0);
      var lost = state.pending.filter(function (p) { return p.op === 'remove' && (!whole || state.base.indexOf(p.id) !== -1); }).length;
      // The account keeps 128 at most: past them the oldest gives way.
      return Math.min(LIMIT, Math.max(0, total + gained - lost));
    }
    var retry = null;
    function reconcile() {
      // A heart the account gained that no card can come for here, or hearts
      // the account now keeps in another order.
      if ((undrawable(root, grid).length || reordered(root, grid)) && redrawFor(state.at)) return;
      var now = counted();
      writeCount(now);
      // A heart saved in another tab brings its card to an empty page.
      if (now > 0) {
        if (empty) empty.hidden = true;
        root.classList.remove('is-empty');
      }
      addCards(root, grid, lacking(root, grid)).then(function () {
        // A heart the account took while its card was on its way, and the
        // card did not come: none will now.
        if (undrawable(root, grid).length && redrawFor(state.at)) return;
        // A card that did not come is asked for again a little later.
        if (!retry && lacking(root, grid).length) retry = setTimeout(function () { retry = null; reconcile(); }, RETRY_MS[0]);
      });
    }
    var opened = counted();
    if (empty) empty.hidden = opened > 0;
    root.classList.toggle('is-empty', opened === 0);
    document.addEventListener('prive:favoritos', reconcile);
    reconcile();
  }

  // ── Start ─────────────────────────────────────────────────────────────

  load();

  window.PriveFavoritos = {
    has: has,
    toggle: toggle,
    ids: function () { return state.ids.slice(); },
    _rules: { cleanIds: cleanIds, setPending: setPending, applyPending: applyPending, settle: settle, startingBase: startingBase, withDrawn: withDrawn },
  };

  if (window.customElements && !window.customElements.get('prive-favorito')) {
    window.customElements.define('prive-favorito', PriveFavorito);
  }
  renderAll();
  Array.prototype.forEach.call(document.querySelectorAll('[data-favoritos-page]'), fillPage);
  if (signedIn && state.pending.length) scheduleFlush(0);
  /**
   * The browser's copy, read again only while it keeps this tab's writes: a
   * change it would not keep is in this tab alone, which then stands, as it
   * does over another tab's on this tab's next change.
   */
  function reread() {
    refresh();
    askWhole();
    renderAll();
  }
  window.addEventListener('pageshow', function (event) {
    // Back from another page: this one may be a stale copy from the cache.
    if (!event.persisted) return;
    var before = state.ids.join(',');
    reread();
    // The Favoritos page draws its cards once: a list changed elsewhere since
    // is drawn afresh.
    if (state.ids.join(',') !== before && document.querySelector('[data-favoritos-page]')) window.location.reload();
  });
  window.addEventListener('storage', function (event) {
    if (event.key === GUEST_KEY || event.key === ACCOUNT_KEY) reread();
  });
})();
